import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
  Typography,
  Avatar,
  Chip,
  Toolbar
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountIcon,
  Assignment as ProjectIcon,
  Code as CodeIcon,
  BugReport as BugIcon,
  Schedule as ScheduleIcon,
  Task as TaskIcon,
  Analytics as AnalyticsIcon,
  Groups as TeamIcon,
  Business as ClientIcon,
  AccountBalance as BillingIcon,
  Notifications as NotificationIcon,
  Help as HelpIcon,
  Security as SecurityIcon,
  Storage as DatabaseIcon,
  Timeline as TimelineIcon,
  Assessment as ReportIcon,
  Folder as FileIcon,
  CalendarToday as CalendarIcon,
  Chat as ChatIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Build as ToolsIcon,
  CloudUpload as DeployIcon,
  GitHub as GitHubIcon,
  Hub as IntegrationIcon,
  Speed as PerformanceIcon,
  MonitorHeart as MonitorIcon,
  Backup as BackupIcon,
  VpnKey as APIIcon,
  Receipt as InvoiceIcon,
  CreditCard as PaymentIcon,
  TrendingUp as GrowthIcon,
  Psychology as AIIcon,
  AutoFixHigh as AutomationIcon,
  Widgets as ComponentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useEnhancedNavigation } from '../../hooks/useEnhancedNavigation';
import DashboardHeader from '../navigation/DashboardHeader';

const drawerWidth = 240;

/**
 * Dashboard Layout Component
 * Common layout for all dashboard pages with comprehensive role-based navigation
 * 
 * Features:
 * - Role-based navigation menus (Admin, Project Manager, Developer, Client)
 * - Collapsible/expandable navigation sections
 * - Active route highlighting
 * - Notification badges for important items
 * - Responsive design with mobile drawer
 * - User info display
 * - Dynamic page titles
 * 
 * Navigation Structure:
 * 
 * ADMIN:
 * - System Overview, User Management, Project Management
 * - Client Management, Financial (Invoices/Payments/Analytics)
 * - Analytics & Reports, System Settings, System Monitor
 * 
 * PROJECT MANAGER:
 * - PM Dashboard, My Projects (Active/Timeline/Planning/Resources)
 * - Team Management, Client Communication, Project Analytics
 * - File Management, Quality Assurance, Reports
 * 
 * DEVELOPER:
 * - Dev Dashboard, My Tasks (Active/Reviews/Testing/Time)
 * - Projects, Development Tools (Git/Deploy/Database/API)
 * - Bug Tracking, Code Analytics, Knowledge Base, Performance Monitor
 * 
 * CLIENT:
 * - Client Dashboard, My Projects (Active/Timeline/Gallery)
 * - Communication (Messages/Calls/Updates)
 * - Billing & Payments, Files & Downloads, Support, Feedback
 */
const DashboardLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, getDashboardPath } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { metrics } = useEnhancedNavigation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Role-based navigation configuration
  const getNavigationItems = () => {
    const role = user?.role;
    
    const commonItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: getDashboardPath() },
      { text: 'Messages', icon: <MessageIcon />, path: '/dashboard/messages' },
      { text: 'Calendar', icon: <CalendarIcon />, path: '/dashboard/calendar' },
      { 
        text: 'Profile & Security', 
        icon: <AccountIcon />, 
        path: '/dashboard/profile',
        children: [
          { text: 'My Profile', icon: <AccountIcon />, path: '/dashboard/profile' },
          { text: 'Session Management', icon: <SecurityIcon />, path: '/dashboard/sessions' },
          { text: 'Security Dashboard', icon: <SecurityIcon />, path: '/dashboard/security' },
          { text: 'Two-Factor Auth', icon: <SecurityIcon />, path: '/dashboard/mfa/setup' },
        ]
      },
    ];

    const roleSpecificItems = {
      admin: [
        { text: 'System Overview', icon: <DashboardIcon />, path: '/dashboard/admin' },
        { 
          text: 'User Management', 
          icon: <PeopleIcon />, 
          path: '/dashboard/admin/users',
          badge: '12',
          children: [
            { text: 'All Users', icon: <TeamIcon />, path: '/dashboard/admin/users' },
            { text: 'Create User', icon: <PeopleIcon />, path: '/dashboard/admin/users/create' },
            { text: 'Roles & Permissions', icon: <SecurityIcon />, path: '/dashboard/admin/users/roles' },
            { text: 'Teams', icon: <TeamIcon />, path: '/dashboard/admin/users/teams' },
          ]
        },
        { 
          text: 'Project Management', 
          icon: <ProjectIcon />, 
          path: '/dashboard/admin/projects',
          children: [
            { text: 'All Projects', icon: <ProjectIcon />, path: '/dashboard/admin/projects' },
            { text: 'Create Project', icon: <ProjectIcon />, path: '/dashboard/admin/projects/create' },
            { text: 'Import Project', icon: <ProjectIcon />, path: '/dashboard/admin/projects/import' },
            { text: 'Project Templates', icon: <ComponentIcon />, path: '/dashboard/admin/projects/templates' },
          ]
        },
        { 
          text: 'Client Management', 
          icon: <ClientIcon />, 
          path: '/dashboard/admin/clients',
          children: [
            { text: 'All Clients', icon: <ClientIcon />, path: '/dashboard/admin/clients' },
            { text: 'Add Client', icon: <ClientIcon />, path: '/dashboard/admin/clients/create' },
            { text: 'Client Portal Access', icon: <SecurityIcon />, path: '/dashboard/admin/clients/portal' },
          ]
        },
        { 
          text: 'Financial', 
          icon: <BillingIcon />, 
          path: '/dashboard/admin/financial',
          children: [
            { text: 'Invoices', icon: <InvoiceIcon />, path: '/dashboard/admin/financial/invoices' },
            { text: 'Payments', icon: <PaymentIcon />, path: '/dashboard/admin/financial/payments' },
            { text: 'Revenue Analytics', icon: <GrowthIcon />, path: '/dashboard/admin/financial/analytics' },
          ]
        },
        { 
          text: 'Analytics & Reports', 
          icon: <AnalyticsIcon />, 
          path: '/dashboard/admin/analytics',
          children: [
            { text: 'Performance Dashboard', icon: <PerformanceIcon />, path: '/dashboard/admin/analytics/performance' },
            { text: 'Project Reports', icon: <ReportIcon />, path: '/dashboard/admin/analytics/projects' },
            { text: 'Team Productivity', icon: <TeamIcon />, path: '/dashboard/admin/analytics/team' },
            { text: 'Financial Reports', icon: <BillingIcon />, path: '/dashboard/admin/analytics/financial' },
          ]
        },
        { 
          text: 'System Settings', 
          icon: <SettingsIcon />, 
          path: '/dashboard/admin/settings',
          children: [
            { text: 'General Settings', icon: <SettingsIcon />, path: '/dashboard/admin/settings' },
            { text: 'Security', icon: <SecurityIcon />, path: '/dashboard/admin/settings/security' },
            { text: 'Integrations', icon: <IntegrationIcon />, path: '/dashboard/admin/settings/integrations' },
            { text: 'Twilio Settings', icon: <MessageIcon />, path: '/dashboard/admin/settings/twilio', badge: 'New' },
            { text: 'Backup & Recovery', icon: <BackupIcon />, path: '/dashboard/admin/settings/backup' },
            { text: 'API Management', icon: <APIIcon />, path: '/dashboard/admin/settings/api' },
          ]
        },
        { text: 'System Monitor', icon: <MonitorIcon />, path: '/dashboard/admin/monitor', badge: '⚠️' },
        { text: 'Help & Support', icon: <HelpIcon />, path: '/dashboard/admin/help', badge: 'New' },
      ],

      project_manager: [
        { text: 'PM Dashboard', icon: <DashboardIcon />, path: '/dashboard/pm' },
        { 
          text: 'My Projects', 
          icon: <ProjectIcon />, 
          path: '/dashboard/pm/projects',
          children: [
            { text: 'Active Projects', icon: <ProjectIcon />, path: '/dashboard/pm/projects/active' },
            { text: 'Project Timeline', icon: <TimelineIcon />, path: '/dashboard/pm/projects/timeline' },
            { text: 'Project Planning', icon: <CalendarIcon />, path: '/dashboard/pm/projects/planning' },
            { text: 'Resource Allocation', icon: <TeamIcon />, path: '/dashboard/pm/projects/resources' },
          ]
        },
        { 
          text: 'Team Management', 
          icon: <TeamIcon />, 
          path: '/dashboard/pm/team',
          children: [
            { text: 'My Team', icon: <TeamIcon />, path: '/dashboard/pm/team' },
            { text: 'Task Assignment', icon: <TaskIcon />, path: '/dashboard/pm/team/tasks' },
            { text: 'Performance Review', icon: <AnalyticsIcon />, path: '/dashboard/pm/team/performance' },
            { text: 'Time Tracking', icon: <ScheduleIcon />, path: '/dashboard/pm/team/time' },
          ]
        },
        { 
          text: 'Client Communication', 
          icon: <ClientIcon />, 
          path: '/dashboard/pm/clients',
          children: [
            { text: 'Client Portal', icon: <ClientIcon />, path: '/dashboard/pm/clients/portal' },
            { text: 'Project Updates', icon: <NotificationIcon />, path: '/dashboard/pm/clients/updates' },
            { text: 'Meetings & Calls', icon: <PhoneIcon />, path: '/dashboard/pm/clients/meetings' },
          ]
        },
        { text: 'Communications', icon: <MessageIcon />, path: '/dashboard/pm/communications', badge: 'New' },
        { 
          text: 'Project Analytics', 
          icon: <AnalyticsIcon />, 
          path: '/dashboard/pm/analytics',
          children: [
            { text: 'Project Progress', icon: <TimelineIcon />, path: '/dashboard/pm/analytics/progress' },
            { text: 'Team Productivity', icon: <TeamIcon />, path: '/dashboard/pm/analytics/productivity' },
            { text: 'Budget Tracking', icon: <BillingIcon />, path: '/dashboard/pm/analytics/budget' },
          ]
        },
        { text: 'File Management', icon: <FileIcon />, path: '/dashboard/pm/files' },
        { text: 'Quality Assurance', icon: <BugIcon />, path: '/dashboard/pm/qa', badge: '3' },
        { text: 'Reports', icon: <ReportIcon />, path: '/dashboard/pm/reports' },
      ],

      developer: [
        { text: 'Dev Dashboard', icon: <DashboardIcon />, path: '/dashboard/developer' },
        { 
          text: 'My Tasks', 
          icon: <TaskIcon />, 
          path: '/dashboard/developer/tasks',
          children: [
            { text: 'Active Tasks', icon: <TaskIcon />, path: '/dashboard/developer/tasks/active' },
            { text: 'Code Reviews', icon: <BugIcon />, path: '/dashboard/developer/tasks/reviews' },
            { text: 'Testing', icon: <BugIcon />, path: '/dashboard/developer/tasks/testing' },
            { text: 'Time Tracking', icon: <ScheduleIcon />, path: '/dashboard/developer/tasks/time' },
          ]
        },
        { 
          text: 'Projects', 
          icon: <ProjectIcon />, 
          path: '/dashboard/developer/projects',
          children: [
            { text: 'My Projects', icon: <ProjectIcon />, path: '/dashboard/developer/projects' },
            { text: 'Project Files', icon: <FileIcon />, path: '/dashboard/developer/projects/files' },
            { text: 'Documentation', icon: <HelpIcon />, path: '/dashboard/developer/projects/docs' },
          ]
        },
        { 
          text: 'Development Tools', 
          icon: <CodeIcon />, 
          path: '/dashboard/developer/tools',
          children: [
            { text: 'Code Repository', icon: <GitHubIcon />, path: '/dashboard/developer/tools/git' },
            { text: 'Deployment', icon: <DeployIcon />, path: '/dashboard/developer/tools/deploy' },
            { text: 'Database Tools', icon: <DatabaseIcon />, path: '/dashboard/developer/tools/database' },
            { text: 'API Testing', icon: <APIIcon />, path: '/dashboard/developer/tools/api' },
          ]
        },
        { 
          text: 'Bug Tracking', 
          icon: <BugIcon />, 
          path: '/dashboard/developer/bugs',
          children: [
            { text: 'My Bugs', icon: <BugIcon />, path: '/dashboard/developer/bugs/assigned' },
            { text: 'Report Bug', icon: <BugIcon />, path: '/dashboard/developer/bugs/create' },
            { text: 'Testing Results', icon: <MonitorIcon />, path: '/dashboard/developer/bugs/testing' },
          ]
        },
        { text: 'Code Analytics', icon: <AnalyticsIcon />, path: '/dashboard/developer/analytics' },
        { text: 'Knowledge Base', icon: <HelpIcon />, path: '/dashboard/developer/knowledge' },
        { text: 'Performance Monitor', icon: <PerformanceIcon />, path: '/dashboard/developer/performance', badge: '!' },
      ],

      client: [
        { text: 'Client Dashboard', icon: <DashboardIcon />, path: '/dashboard/client' },
        { 
          text: 'My Projects', 
          icon: <ProjectIcon />, 
          path: '/dashboard/client/projects',
          children: [
            { text: 'Active Projects', icon: <ProjectIcon />, path: '/dashboard/client/projects/active' },
            { text: 'Project Timeline', icon: <TimelineIcon />, path: '/dashboard/client/projects/timeline' },
            { text: 'Project Gallery', icon: <FileIcon />, path: '/dashboard/client/projects/gallery' },
          ]
        },
        { 
          text: 'Communication', 
          icon: <MessageIcon />, 
          path: '/dashboard/client/communication',
          children: [
            { text: 'Messages', icon: <MessageIcon />, path: '/dashboard/client/communication/messages' },
            { text: 'Video Calls', icon: <PhoneIcon />, path: '/dashboard/client/communication/calls' },
            { text: 'Project Updates', icon: <NotificationIcon />, path: '/dashboard/client/communication/updates' },
          ]
        },
        { 
          text: 'Billing & Payments', 
          icon: <BillingIcon />, 
          path: '/dashboard/client/billing',
          children: [
            { text: 'Invoices', icon: <InvoiceIcon />, path: '/dashboard/client/billing/invoices' },
            { text: 'Payment History', icon: <PaymentIcon />, path: '/dashboard/client/billing/payments' },
            { text: 'Billing Settings', icon: <SettingsIcon />, path: '/dashboard/client/billing/settings' },
          ]
        },
        { 
          text: 'Files & Downloads', 
          icon: <FileIcon />, 
          path: '/dashboard/client/files',
          children: [
            { text: 'Project Files', icon: <FileIcon />, path: '/dashboard/client/files/projects' },
            { text: 'Downloads', icon: <DeployIcon />, path: '/dashboard/client/files/downloads' },
            { text: 'File Sharing', icon: <FileIcon />, path: '/dashboard/client/files/sharing' },
          ]
        },
        { text: 'Support', icon: <HelpIcon />, path: '/dashboard/client/support' },
        { text: 'Feedback', icon: <ChatIcon />, path: '/dashboard/client/feedback' },
        { text: 'Account Settings', icon: <SettingsIcon />, path: '/dashboard/client/settings' },
      ]
    };

    return roleSpecificItems[role as keyof typeof roleSpecificItems] || commonItems;
  };

  // Add metrics-based badge updates to navigation items
  const updateNavigationWithMetrics = (items: any[]) => {
    return items.map(item => {
      // Update badges based on real-time metrics
      switch (item.text) {
        case 'Messages':
          return { ...item, badge: metrics.unreadMessages > 0 ? metrics.unreadMessages : undefined };
        case 'My Tasks':
        case 'Active Tasks':
          return { ...item, badge: metrics.pendingTasks > 0 ? metrics.pendingTasks : undefined };
        case 'System Monitor':
          return { ...item, badge: metrics.systemAlerts > 0 ? '⚠️' : undefined };
        case 'Notifications':
          return { ...item, badge: metrics.notifications > 0 ? metrics.notifications : undefined };
        default:
          return item;
      }
    });
  };

  const navigationItems = updateNavigationWithMetrics(getNavigationItems());
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const handleItemExpand = (itemText: string) => {
    setExpandedItems(prev => 
      prev.includes(itemText) 
        ? prev.filter(item => item !== itemText)
        : [...prev, itemText]
    );
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const renderNavigationItem = (item: any, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.text);
    const isActive = !hasChildren && isActiveRoute(item.path);
    
    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ pl: level * 2 }}>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                handleItemExpand(item.text);
              } else {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }
            }}
            sx={{
              minHeight: 48,
              px: 2.5,
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: isActive 
                  ? 'rgba(25, 118, 210, 0.12)' 
                  : 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 2,
                justifyContent: 'center',
                color: isActive ? 'primary.main' : 'text.secondary',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontSize: level === 0 ? '0.875rem' : '0.8125rem',
                fontWeight: isActive ? 600 : (level === 0 ? 500 : 400),
                color: isActive 
                  ? 'primary.main' 
                  : (level === 0 ? 'text.primary' : 'text.secondary'),
              }}
            />
            
            {/* Badge for notifications/counts */}
            {item.badge && (
              <Chip
                label={item.badge}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  bgcolor: 'error.main',
                  color: 'white',
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
            )}
            
            {hasChildren && (
              <Box sx={{ ml: 1 }}>
                {isExpanded ? 
                  <KeyboardArrowDownIcon sx={{ fontSize: 20, color: 'text.secondary' }} /> : 
                  <KeyboardArrowRightIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                }
              </Box>
            )}
          </ListItemButton>
        </ListItem>
        
        {hasChildren && isExpanded && (
          <Box>
            {item.children.map((child: any) => renderNavigationItem(child, level + 1))}
          </Box>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2 }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700, color: 'primary.main' }}>
          VegaDigital
        </Typography>
      </Toolbar>
      <Divider />
      
      {/* User Info Section */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
            {user?.name?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
              {user?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.role?.replace('_', ' ').toUpperCase() || 'USER'}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider />
      
      {/* Navigation Items */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <List disablePadding>
          {navigationItems.map((item) => renderNavigationItem(item))}
        </List>
      </Box>
      
      {/* Footer Section */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          VegaDigital v2.0
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Enhanced Dashboard Header */}
      <DashboardHeader 
        drawerWidth={drawerWidth}
        onDrawerToggle={handleDrawerToggle}
      />

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;